import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { CAP } from '../utils/permissions';
import { usePermissions, ReadOnlyNotice } from '../utils/PermissionContext';
import { errorText } from '../utils/apiErrors';
import { apiFetch } from '../utils/apiAuth';

const AdminRenewalsSkeleton = ({ t }) => {
    const dummyWeeks = ['W1', 'W2', 'W3', 'W4', 'W5', 'W6'];
    return (
        <div className="p-6 max-w-full overflow-x-auto animate-pulse">
            {/* Header Skeleton: Title and controls */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-3">
                    <div className="h-7 w-7 bg-gray-200 rounded-full"></div>
                    <div className="h-7 bg-gray-200 rounded w-64"></div>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                    <div className="h-8 bg-gray-200 rounded w-36"></div>
                    <div className="h-8 bg-gray-200 rounded w-full sm:w-64"></div>
                </div>
            </div>

            {/* Filter chips skeleton: Period */}
            <div className="flex flex-wrap gap-2 mb-3">
                {[1, 2, 3, 4, 5, 6, 7, 8].map((idx) => (
                    <div key={idx} className="h-7 w-20 bg-gray-200 rounded-full"></div>
                ))}
            </div>

            {/* Filter chips skeleton: Status */}
            <div className="flex flex-wrap gap-2 mb-6">
                {[1, 2, 3, 4].map((idx) => (
                    <div key={idx} className="h-7 w-24 bg-gray-200 rounded-full"></div>
                ))}
            </div>

            {/* Quick info skeleton cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="h-24 bg-gray-100 border border-gray-200 rounded-xl"></div>
                <div className="h-24 bg-gray-100 border border-gray-200 rounded-xl"></div>
            </div>

            {/* Table skeleton */}
            <div className="inline-block min-w-full align-middle">
                <div className="border rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-[#8b236a] text-white opacity-85">
                            <tr>
                                <th className="px-4 py-3 text-left font-medium w-48">{t.sku || 'SKU'}</th>
                                <th className="px-4 py-3 text-center font-medium border-l border-white/20 w-24">Weeks →</th>
                                {dummyWeeks.map((week, idx) => (
                                    <th key={idx} className="px-3 py-3 text-center font-medium border-l border-white/20 w-16">
                                        {week}
                                    </th>
                                ))}
                                <th className="px-4 py-3 text-center font-medium border-l border-white/20 w-28">
                                    {t.total_licenses || 'Total Licenses'}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {[1, 2, 3, 4, 5].map((rowIdx) => (
                                <tr key={rowIdx}>
                                    <td className="px-4 py-3 sticky left-0 bg-white border-r">
                                        <div className="h-4 bg-gray-200 rounded w-36"></div>
                                    </td>
                                    <td className="px-4 py-3 border-r">
                                        <div className="h-4 bg-gray-200 rounded w-8 mx-auto"></div>
                                    </td>
                                    {dummyWeeks.map((_, colIdx) => (
                                        <td key={colIdx} className="px-3 py-3 border-r">
                                            <div className="h-4 bg-gray-200 rounded w-6 mx-auto"></div>
                                        </td>
                                    ))}
                                    <td className="px-4 py-3 bg-gray-50">
                                        <div className="h-4 bg-gray-200 rounded w-8 mx-auto"></div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const SortIcon = ({ direction, isWhite = false }) => {
    if (!direction) {
        return (
            <svg className={`w-3.5 h-3.5 ${isWhite ? 'text-white/40 group-hover:text-white/80' : 'text-gray-400 opacity-40 group-hover:opacity-80'} transition-opacity flex-shrink-0`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
            </svg>
        );
    }
    return (
        <svg className={`w-3.5 h-3.5 ${isWhite ? 'text-white' : 'text-[#8b236a]'} flex-shrink-0`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {direction === 'asc' ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 15l7-7 7 7" />
            ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
            )}
        </svg>
    );
};

const PostpaidPrepaidBadge = ({ isPostpaid, t }) => (
    <span
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider select-none border shrink-0 ${
            isPostpaid
                ? 'bg-amber-100 text-amber-800 border-amber-300'
                : 'bg-teal-100 text-teal-800 border-teal-300'
        }`}
    >
        <span className={`h-1.5 w-1.5 rounded-full ${isPostpaid ? 'bg-amber-600' : 'bg-teal-600'}`} />
        {isPostpaid ? (t?.postpaid || 'Postpaid') : (t?.prepaid || 'Prepaid')}
    </span>
);

const getStatusChipClasses = (status, isActive) => {
    if (!status || status === 'ALL') {
        return isActive
            ? 'bg-primary text-white border-primary shadow-sm shadow-primary/20'
            : 'bg-white hover:bg-gray-50 text-gray-600 border-gray-200';
    }
    switch (status) {
        case 'CANCEL':
            return isActive
                ? 'bg-amber-500 text-white border-amber-500 shadow-sm shadow-amber-500/20'
                : 'bg-white hover:bg-amber-50/50 text-amber-700 border-amber-200';
        case 'RENEW_CURRENT_USERS':
            return isActive
                ? 'bg-orange-500 text-white border-orange-500 shadow-sm shadow-orange-500/20'
                : 'bg-white hover:bg-orange-50/50 text-orange-700 border-orange-200';
        case 'RENEW_CURRENT_USERS_YEARLY_PAY':
            return isActive
                ? 'bg-orange-600 text-white border-orange-600 shadow-sm shadow-orange-600/20'
                : 'bg-white hover:bg-orange-50/50 text-orange-800 border-orange-200';
        case 'SUSPENDED':
            return isActive
                ? 'bg-rose-600 text-white border-rose-600 shadow-sm shadow-rose-600/20'
                : 'bg-white hover:bg-rose-50/50 text-rose-700 border-rose-200';
        case 'AUTO_RENEW':
            return isActive
                ? 'bg-sky-600 text-white border-sky-600 shadow-sm shadow-sky-600/20'
                : 'bg-white hover:bg-sky-50/50 text-sky-700 border-sky-200';
        case 'MANUAL':
            return isActive
                ? 'bg-purple-600 text-white border-purple-600 shadow-sm shadow-purple-600/20'
                : 'bg-white hover:bg-purple-50/50 text-purple-700 border-purple-200';
        case 'SWITCH_TO_PAY_AS_YOU_GO':
            return isActive
                ? 'bg-teal-600 text-white border-teal-600 shadow-sm shadow-teal-600/20'
                : 'bg-white hover:bg-teal-50/50 text-teal-700 border-teal-200';
        default:
            return isActive
                ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm shadow-indigo-600/20'
                : 'bg-white hover:bg-indigo-50/50 text-indigo-700 border-indigo-200';
    }
};

const RenewalTypeBadge = ({ renewalType }) => (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border shrink-0 ${getStatusChipClasses(renewalType || 'MANUAL', false)}`}>
        {renewalType || 'MANUAL'}
    </span>
);

const SubscriptionStatusBadge = ({ status }) => {
    const isSuspended = status === 'SUSPENDED';
    const isActive = status === 'ACTIVE';

    return (
        <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold border shrink-0 ${
                isActive
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : isSuspended
                    ? 'bg-rose-50 text-rose-700 border-rose-200'
                    : 'bg-gray-50 text-gray-700 border-gray-200'
            }`}
            title={`Google Status: ${status || 'Unknown'}`}
        >
            <span className={`h-1.5 w-1.5 rounded-full mr-1.5 ${
                isActive ? 'bg-emerald-500' : isSuspended ? 'bg-rose-500' : 'bg-gray-400'
            }`} />
            {status || 'UNKNOWN'}
        </span>
    );
};



function AdminRenewals({ t, language }) {
    const { can } = usePermissions();
    const canWrite = can(CAP.ADMIN_WRITE);
    const [loading, setLoading] = useState(true);
    const [summaryData, setSummaryData] = useState({ current_week: '', weeks: [], data: [] });
    const [selectedCellData, setSelectedCellData] = useState(null);
    const [daysFilter, setDaysFilter] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [isChecking, setIsChecking] = useState(false);
    const [quickInfoModalType, setQuickInfoModalType] = useState(null); // 'suspended' | 'going_suspend'
    const [quickModalSearch, setQuickModalSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState(null);
    const [modalStatusFilter, setModalStatusFilter] = useState(null);
    const [modalSortConfig, setModalSortConfig] = useState({ key: null, direction: 'asc' });
    const [cellSortConfig, setCellSortConfig] = useState({ key: null, direction: 'asc' });
    const apiUrl = process.env.REACT_APP_API_URL || '';

    useEffect(() => {
        fetchSummary();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchSummary = async () => {
        try {
            const response = await apiFetch(`${apiUrl}/api/admin/renewals-summary`);

            const data = await response.json();

            if (response.ok) {
                setSummaryData(data);
            } else {
                toast.error(errorText(data, t, 'Failed to fetch summary data'));
            }
        } catch (error) {
            console.error('Error fetching renewals summary:', error);
            toast.error('An error occurred while fetching the summary');
        } finally {
            setLoading(false);
        }
    };

    const handleCheckRenewals = async () => {
        setIsChecking(true);
        try {
            const res = await apiFetch(`${apiUrl}/api/check_renewals`, {
                method: 'POST'
            });
            const result = await res.json();
            if (result.success) {
                toast.success(t.check_renewals_started || "The background job to check renewals has started!");
            } else {
                toast.error(errorText(result, t, "Failed to trigger renewals job."));
            }
        } catch (error) {
            console.error("Error triggering renewals job:", error);
            toast.error("An error occurred while triggering the renewals job.");
        } finally {
            setIsChecking(false);
        }
    };

    const handleCustomerClick = (customerId) => {
        if (customerId) {
            localStorage.setItem('selectedCustomer', JSON.stringify({ customerId }));
            window.open('/', '_blank');
        }
    };

    const { current_week: currentWeek, weeks, data } = summaryData;

    const formatSkuName = (name) => {
        if (!name) return '';
        return name.replace(/\bGoogle Workspace\b/g, 'GW');
    };

    const isAutoRenewSub = (sub) => {
        const autoRenewTypes = ['AUTO_RENEW', 'RENEW_CURRENT_COMMITMENT', 'AUTO_RENEW_YEARLY_PAY', 'AUTO_RENEW_MONTHLY_PAY'];
        return (
            autoRenewTypes.includes(sub.renewalType) ||
            (typeof sub.renewalType === 'string' && sub.renewalType.toUpperCase().includes('AUTO_RENEW')) ||
            sub.showRenewalReminder === false
        );
    };

    const getWeekOffsetNum = (weekStr, currentWeekStr) => {
        if (!weekStr || !currentWeekStr) return null;
        const [wYear, wNum] = weekStr.split('-W').map(Number);
        const [cYear, cNum] = currentWeekStr.split('-W').map(Number);
        if (wYear === cYear) {
            return wNum - cNum;
        }
        return (wYear - cYear) * 52 + (wNum - cNum);
    };

    const getPastWeekOffset = (weekStr, currentWeekStr) => {
        const diff = getWeekOffsetNum(weekStr, currentWeekStr);
        if (diff !== null && diff < 0) return `${diff}w`;
        return null;
    };

    const parseDate = (dateStr) => {
        if (!dateStr) return new Date(0);
        const [d, m, y] = dateStr.split('/');
        return new Date(y, m - 1, d);
    };

    const getDaysUntil = (dateStr) => {
        const target = parseDate(dateStr);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const diffTime = target - today;
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    // Calculate suspended and going-to-suspend subscriptions within +/- 2 weeks
    const { suspendedList, goingSuspendList } = useMemo(() => {
        const suspended = [];
        const going = [];
        const seenSuspended = new Set();
        const seenGoing = new Set();

        data.forEach(row => {
            weeks.forEach(week => {
                const offset = getWeekOffsetNum(week, currentWeek);
                if (offset !== null && offset >= -2 && offset <= 2) {
                    const details = row[week]?.details || [];
                    details.forEach(sub => {
                        const key = `${sub.customerId || sub.customerDomain}_${sub.subscriptionId || sub.skuName}_${week}`;
                        const item = {
                            ...sub,
                            sku: row.sku,
                            week,
                            weekOffset: offset,
                        };
                        if (sub.status === 'SUSPENDED') {
                            if (!seenSuspended.has(key)) {
                                seenSuspended.add(key);
                                suspended.push(item);
                            }
                        } else if (sub.status === 'ACTIVE' && !isAutoRenewSub(sub)) {
                            if (!seenGoing.has(key)) {
                                seenGoing.add(key);
                                going.push(item);
                            }
                        }
                    });
                }
            });
        });
        return { suspendedList: suspended, goingSuspendList: going };
    }, [data, weeks, currentWeek]);

    const suspendedCustomerCount = useMemo(() => {
        return new Set(suspendedList.map(s => s.customerId || s.customerDomain)).size;
    }, [suspendedList]);

    const suspendedLicenseCount = useMemo(() => {
        return suspendedList.reduce((sum, s) => sum + (s.licenses || 0), 0);
    }, [suspendedList]);

    const goingSuspendCustomerCount = useMemo(() => {
        return new Set(goingSuspendList.map(s => s.customerId || s.customerDomain)).size;
    }, [goingSuspendList]);

    const goingSuspendLicenseCount = useMemo(() => {
        return goingSuspendList.reduce((sum, s) => sum + (s.licenses || 0), 0);
    }, [goingSuspendList]);

    const activeModalList = useMemo(() => {
        if (quickInfoModalType === 'suspended') return suspendedList;
        if (quickInfoModalType === 'going_suspend') return goingSuspendList;
        return [];
    }, [quickInfoModalType, suspendedList, goingSuspendList]);

    const availableStatuses = useMemo(() => {
        const statuses = new Set();
        data.forEach(row => {
            weeks.forEach(week => {
                (row[week]?.details || []).forEach(sub => {
                    const s = sub.status === 'SUSPENDED' ? 'SUSPENDED' : (sub.renewalType || 'MANUAL');
                    if (s) statuses.add(s);
                });
            });
        });
        return Array.from(statuses).sort();
    }, [data, weeks]);

    const modalAvailableStatuses = useMemo(() => {
        const statuses = new Set();
        activeModalList.forEach(item => {
            const s = quickInfoModalType === 'suspended' ? 'SUSPENDED' : (item.renewalType || 'MANUAL');
            if (s) statuses.add(s);
        });
        return Array.from(statuses).sort();
    }, [activeModalList, quickInfoModalType]);

    const filteredModalList = useMemo(() => {
        let list = activeModalList;
        if (modalStatusFilter) {
            list = list.filter(item => {
                const s = quickInfoModalType === 'suspended' ? 'SUSPENDED' : (item.renewalType || 'MANUAL');
                return s === modalStatusFilter;
            });
        }
        if (!quickModalSearch.trim()) return list;
        const q = quickModalSearch.toLowerCase();
        return list.filter(item => {
            const s = (quickInfoModalType === 'suspended' ? 'suspended' : (item.renewalType || 'manual')).toLowerCase();
            return (
                (item.customerName || '').toLowerCase().includes(q) ||
                (item.customerDomain || '').toLowerCase().includes(q) ||
                (item.sku || '').toLowerCase().includes(q) ||
                (item.isPostpaid ? 'postpaid' : 'prepaid').includes(q) ||
                s.includes(q)
            );
        });
    }, [activeModalList, modalStatusFilter, quickModalSearch, quickInfoModalType]);

    const handleModalSort = (key) => {
        setModalSortConfig(prev => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
    };

    const handleCellSort = (key) => {
        setCellSortConfig(prev => {
            if (prev.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
    };

    const sortedModalList = useMemo(() => {
        if (!modalSortConfig.key) return filteredModalList;
        const { key, direction } = modalSortConfig;
        const multiplier = direction === 'asc' ? 1 : -1;

        return [...filteredModalList].sort((a, b) => {
            if (key === 'customer') {
                const nameA = a.customerName || a.customerDomain || '';
                const nameB = b.customerName || b.customerDomain || '';
                return multiplier * nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
            }
            if (key === 'sku') {
                const skuA = formatSkuName(a.sku) || '';
                const skuB = formatSkuName(b.sku) || '';
                return multiplier * skuA.localeCompare(skuB, undefined, { sensitivity: 'base' });
            }
            if (key === 'week') {
                const weekA = a.week || '';
                const weekB = b.week || '';
                return multiplier * weekA.localeCompare(weekB);
            }
            if (key === 'licenses') {
                const licA = Number(a.licenses) || 0;
                const licB = Number(b.licenses) || 0;
                return multiplier * (licA - licB);
            }
            if (key === 'renewalDate') {
                const dateA = parseDate(a.renewalDate).getTime();
                const dateB = parseDate(b.renewalDate).getTime();
                return multiplier * (dateA - dateB);
            }
            if (key === 'renewalType') {
                const typeA = a.renewalType || 'MANUAL';
                const typeB = b.renewalType || 'MANUAL';
                return multiplier * typeA.localeCompare(typeB);
            }
            if (key === 'status') {
                const statA = a.status || '';
                const statB = b.status || '';
                return multiplier * statA.localeCompare(statB);
            }
            return 0;
        });
    }, [filteredModalList, modalSortConfig]);

    const sortedCellDetails = useMemo(() => {
        if (!selectedCellData?.details) return [];
        if (!cellSortConfig.key) return selectedCellData.details;
        const { key, direction } = cellSortConfig;
        const multiplier = direction === 'asc' ? 1 : -1;

        return [...selectedCellData.details].sort((a, b) => {
            if (key === 'customer') {
                const nameA = a.customerName || a.customerDomain || '';
                const nameB = b.customerName || b.customerDomain || '';
                return multiplier * nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
            }
            if (key === 'domain') {
                const domA = a.customerDomain || '';
                const domB = b.customerDomain || '';
                return multiplier * domA.localeCompare(domB, undefined, { sensitivity: 'base' });
            }
            if (key === 'licenses') {
                const licA = Number(a.licenses) || 0;
                const licB = Number(b.licenses) || 0;
                return multiplier * (licA - licB);
            }
            if (key === 'renewalDate') {
                const dateA = parseDate(a.renewalDate).getTime();
                const dateB = parseDate(b.renewalDate).getTime();
                return multiplier * (dateA - dateB);
            }
            if (key === 'renewalType') {
                const typeA = a.renewalType || 'MANUAL';
                const typeB = b.renewalType || 'MANUAL';
                return multiplier * typeA.localeCompare(typeB);
            }
            if (key === 'status') {
                const statA = a.status || '';
                const statB = b.status || '';
                return multiplier * statA.localeCompare(statB);
            }
            return 0;
        });
    }, [selectedCellData, cellSortConfig]);

    const filteredData = data.map(row => {
        const newRow = { sku: row.sku };
        weeks.forEach(week => {
            if (row[week]) {
                const offset = getWeekOffsetNum(week, currentWeek);
                const isWithin2w = offset !== null && offset >= -2 && offset <= 2;

                const filteredDetails = row[week].details.filter(sub => {
                    const daysUntil = getDaysUntil(sub.renewalDate);
                    let matchesDays = true;
                    if (daysFilter === 'past') {
                        matchesDays = daysUntil < 0;
                    } else if (daysFilter === 'suspended_2w') {
                        matchesDays = isWithin2w && sub.status === 'SUSPENDED';
                    } else if (daysFilter === 'going_suspend_2w') {
                        matchesDays = isWithin2w && sub.status === 'ACTIVE' && !isAutoRenewSub(sub);
                    } else if (typeof daysFilter === 'number') {
                        matchesDays = daysUntil <= daysFilter && daysUntil >= 0;
                    }

                    const itemStatus = sub.status === 'SUSPENDED' ? 'SUSPENDED' : (sub.renewalType || 'MANUAL');
                    const matchesStatus = !statusFilter || itemStatus === statusFilter;

                    const matchesSearch = searchQuery === '' ||
                        (sub.customerName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                        (sub.customerDomain || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                        (sub.isPostpaid ? 'postpaid' : 'prepaid').includes(searchQuery.toLowerCase()) ||
                        itemStatus.toLowerCase().includes(searchQuery.toLowerCase());
                    return matchesDays && matchesStatus && matchesSearch;
                });

                if (filteredDetails.length > 0) {
                    newRow[week] = {
                        count: filteredDetails.reduce((sum, sub) => sum + sub.licenses, 0),
                        details: filteredDetails
                    };
                }
            }
        });
        return newRow;
    }).filter(row => weeks.some(week => row[week]));

    const weekTotals = weeks.reduce((acc, week) => {
        acc[week] = filteredData.reduce((sum, row) => sum + (row[week]?.count || 0), 0);
        return acc;
    }, {});

    const grandTotal = Object.values(weekTotals).reduce((a, b) => a + b, 0);

    if (loading) {
        return <AdminRenewalsSkeleton t={t} />;
    }

    return (
        <div className="p-6 max-w-full overflow-x-auto">
            {/* Header: Title on Left, Controls (Check Renewals & Search) on Right */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                <h2 className="text-2xl font-bold flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 mr-3 bg-primary text-white rounded-full p-1.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    {t.admin_renewals_summary}
                </h2>

                <div className="flex flex-col sm:flex-row items-center gap-2 w-full md:w-auto">
                    {canWrite && <button
                        onClick={handleCheckRenewals}
                        disabled={isChecking}
                        className={`flex items-center justify-center px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${isChecking ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'} text-white shadow-sm whitespace-nowrap w-full sm:w-auto`}
                    >
                        {isChecking ? (
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                        ) : (
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                        )}
                        {t.check_renewals || "Check Renewals"}
                    </button>}
                    <input
                        type="text"
                        placeholder={t.search_customer || "Search customer..."}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-1.5 text-sm w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-[#8b236a]"
                    />
                </div>
            </div>

            <ReadOnlyNotice t={t} />

            {/* Filter Chips: Period */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="text-xs font-semibold uppercase text-gray-500 mr-1 tracking-wider">{t.period || 'Periode'}:</span>
                <button
                    onClick={() => setDaysFilter(null)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${daysFilter === null ? 'bg-[#8b236a] text-white shadow-xs' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                    {t.filter_all || 'Alles'}
                </button>
                <button
                    onClick={() => setDaysFilter('past')}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${daysFilter === 'past' ? 'bg-[#8b236a] text-white shadow-xs' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                    {t.filter_past_weeks || 'Verstreken (-2w / -1w)'}
                </button>
                <button
                    onClick={() => setDaysFilter(daysFilter === 'suspended_2w' ? null : 'suspended_2w')}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${daysFilter === 'suspended_2w' ? 'bg-[#8b236a] text-white shadow-xs' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                    {t.filter_suspended_2w || 'Opgeschort (±2w)'}
                </button>
                <button
                    onClick={() => setDaysFilter(daysFilter === 'going_suspend_2w' ? null : 'going_suspend_2w')}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${daysFilter === 'going_suspend_2w' ? 'bg-[#8b236a] text-white shadow-xs' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                    {t.filter_going_suspend_2w || 'Dreigt Opgeschort (±2w)'}
                </button>
                {[7, 15, 30, 60, 90].map(days => (
                    <button
                        key={days}
                        onClick={() => setDaysFilter(days)}
                        className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${daysFilter === days ? 'bg-[#8b236a] text-white shadow-xs' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                    >
                        {t[`filter_${days}_days`] || `≤ ${days} Dagen`}
                    </button>
                ))}
            </div>

            {/* Filter Chips: Status */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider select-none shrink-0 mr-1">
                    {t?.status || 'Status'}:
                </span>
                <button
                    onClick={() => setStatusFilter(null)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-bold border transition duration-200 shrink-0 select-none ${
                        getStatusChipClasses('ALL', statusFilter === null)
                    }`}
                >
                    {t?.all_statuses || t?.filter_all || 'Alles'}
                </button>
                {availableStatuses.map(status => {
                    const isActive = statusFilter === status;
                    return (
                        <button
                            key={status}
                            onClick={() => setStatusFilter(isActive ? null : status)}
                            className={`px-3.5 py-1.5 rounded-full text-xs font-bold border transition duration-200 shrink-0 select-none ${
                                getStatusChipClasses(status, isActive)
                            }`}
                        >
                            {status}
                        </button>
                    );
                })}
            </div>

            {/* Quick Information Cards for +/- 2 weeks */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {/* Suspended Customers Card */}
                <div className={`relative overflow-hidden rounded-xl border p-4 transition-all shadow-xs ${
                    daysFilter === 'suspended_2w' 
                        ? 'bg-rose-50/90 border-rose-400 ring-2 ring-rose-300' 
                        : 'bg-white border-rose-200 hover:border-rose-300 hover:shadow-sm'
                }`}>
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                            <div className="p-2.5 rounded-lg bg-rose-100 text-rose-700 flex-shrink-0 mt-0.5">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h3 className="font-semibold text-gray-900 text-base">
                                        {t.quick_info_suspended_title || 'Suspended Customers (±2w)'}
                                    </h3>
                                    <span className="px-2 py-0.5 rounded-full text-[11px] font-bold uppercase bg-rose-100 text-rose-800 tracking-wide">
                                        {suspendedCustomerCount}
                                    </span>
                                </div>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    {t.quick_info_suspended_desc || 'Already suspended Google Workspace accounts within ±2 weeks.'}
                                </p>
                                <div className="flex items-baseline gap-3 mt-2 text-sm">
                                    <span className="font-bold text-lg text-rose-700">
                                        {suspendedCustomerCount} <span className="text-xs font-normal text-gray-600">{t.quick_info_customers || 'customers'}</span>
                                    </span>
                                    <span className="text-gray-300">•</span>
                                    <span className="font-semibold text-gray-700">
                                        {suspendedLicenseCount} <span className="text-xs font-normal text-gray-500">{t.quick_info_licenses || 'licenses'}</span>
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2 self-start flex-shrink-0">
                            <button
                                onClick={() => setQuickInfoModalType('suspended')}
                                className="px-3 py-1.5 text-xs font-medium bg-white text-rose-700 border border-rose-300 rounded-lg hover:bg-rose-50 transition-colors shadow-2xs"
                            >
                                {t.quick_info_view_list || 'View List'}
                            </button>
                            <button
                                onClick={() => setDaysFilter(daysFilter === 'suspended_2w' ? null : 'suspended_2w')}
                                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors shadow-2xs ${
                                    daysFilter === 'suspended_2w'
                                        ? 'bg-rose-700 text-white hover:bg-rose-800'
                                        : 'bg-rose-100 text-rose-800 hover:bg-rose-200'
                                }`}
                            >
                                {daysFilter === 'suspended_2w' ? (t.quick_info_filtering || 'Filtered') : (t.quick_info_filter_table || 'Filter Table')}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Going to Suspend Customers Card */}
                <div className={`relative overflow-hidden rounded-xl border p-4 transition-all shadow-xs ${
                    daysFilter === 'going_suspend_2w' 
                        ? 'bg-amber-50/90 border-amber-400 ring-2 ring-amber-300' 
                        : 'bg-white border-amber-200 hover:border-amber-300 hover:shadow-sm'
                }`}>
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                            <div className="p-2.5 rounded-lg bg-amber-100 text-amber-700 flex-shrink-0 mt-0.5">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h3 className="font-semibold text-gray-900 text-base">
                                        {t.quick_info_going_suspend_title || 'Going to Suspend (±2w)'}
                                    </h3>
                                    <span className="px-2 py-0.5 rounded-full text-[11px] font-bold uppercase bg-amber-100 text-amber-800 tracking-wide">
                                        {goingSuspendCustomerCount}
                                    </span>
                                </div>
                                <p className="text-xs text-gray-500 mt-0.5">
                                    {t.quick_info_going_suspend_desc || 'Active accounts within ±2 weeks with auto-renew OFF (at risk).'}
                                </p>
                                <div className="flex items-baseline gap-3 mt-2 text-sm">
                                    <span className="font-bold text-lg text-amber-700">
                                        {goingSuspendCustomerCount} <span className="text-xs font-normal text-gray-600">{t.quick_info_customers || 'customers'}</span>
                                    </span>
                                    <span className="text-gray-300">•</span>
                                    <span className="font-semibold text-gray-700">
                                        {goingSuspendLicenseCount} <span className="text-xs font-normal text-gray-500">{t.quick_info_licenses || 'licenses'}</span>
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2 self-start flex-shrink-0">
                            <button
                                onClick={() => setQuickInfoModalType('going_suspend')}
                                className="px-3 py-1.5 text-xs font-medium bg-white text-amber-700 border border-amber-300 rounded-lg hover:bg-amber-50 transition-colors shadow-2xs"
                            >
                                {t.quick_info_view_list || 'View List'}
                            </button>
                            <button
                                onClick={() => setDaysFilter(daysFilter === 'going_suspend_2w' ? null : 'going_suspend_2w')}
                                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors shadow-2xs ${
                                    daysFilter === 'going_suspend_2w'
                                        ? 'bg-amber-700 text-white hover:bg-amber-800'
                                        : 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                                }`}
                            >
                                {daysFilter === 'going_suspend_2w' ? (t.quick_info_filtering || 'Filtered') : (t.quick_info_filter_table || 'Filter Table')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {filteredData.length === 0 ? (
                <p className="text-gray-500">{t.no_data_found}</p>
            ) : (
                <div className="inline-block min-w-full align-middle">
                    <div className="border rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200 text-sm cursor-default">
                            <thead className="bg-[#8b236a] text-white">
                                <tr>
                                    <th className="px-4 py-3 text-left font-medium sticky left-0 z-10" style={{ backgroundColor: '#8b236a' }}>{t.sku}</th>
                                    <th className="px-4 py-3 text-center font-medium border-l border-white/20 whitespace-nowrap bg-[#a4638d]">Weeks →</th>
                                    {weeks.map(week => {
                                        const isCurrent = currentWeek && week === currentWeek;
                                        const isPast = currentWeek && week < currentWeek;
                                        const pastOffset = isPast ? getPastWeekOffset(week, currentWeek) : null;

                                        return (
                                            <th
                                                key={week}
                                                className={`px-3 py-2 text-center font-medium border-l border-white/20 whitespace-nowrap transition-colors ${
                                                    isCurrent
                                                        ? 'bg-[#6b144f] ring-2 ring-yellow-400 z-10'
                                                        : isPast
                                                        ? 'bg-[#7c3060]'
                                                        : 'bg-[#a4638d]'
                                                }`}
                                                title={isCurrent ? `${week} (${t.current_week || 'Current Week'})` : isPast ? `${week} (${pastOffset || 'Past Week'})` : week}
                                            >
                                                <div className="flex flex-col items-center justify-center">
                                                    <span className="font-semibold">{week.replace(/^\d{4}-W/, '')}</span>
                                                    {isCurrent && (
                                                        <span className="text-[10px] font-bold text-yellow-300 uppercase tracking-wider leading-none mt-0.5">
                                                            {t.current || 'Nu'}
                                                        </span>
                                                    )}
                                                    {isPast && (
                                                        <span className="text-[9px] font-medium text-pink-200 leading-none mt-0.5">
                                                            {pastOffset || '-'}
                                                        </span>
                                                    )}
                                                </div>
                                            </th>
                                        );
                                    })}
                                    <th className="px-4 py-3 text-center font-medium border-l border-white/20" style={{ backgroundColor: '#8b236a' }}>
                                        {t.total_licenses}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredData.map((row, idx) => {
                                    const rowTotal = weeks.reduce((sum, w) => sum + (row[w]?.count || 0), 0);

                                    return (
                                        <tr key={idx} className="hover:bg-purple-50 transition-colors">
                                            <td className="px-4 py-2 text-gray-900 font-medium sticky left-0 bg-white border-r" style={{ zIndex: 5 }} title={row.sku}>
                                                {formatSkuName(row.sku)}
                                            </td>
                                            <td className="px-4 py-2 text-center text-gray-500 border-r">{rowTotal}</td>
                                            {weeks.map(week => {
                                                const isCurrent = currentWeek && week === currentWeek;
                                                const isPast = currentWeek && week < currentWeek;
                                                const cellData = row[week];
                                                const hasCount = cellData?.count > 0;

                                                return (
                                                    <td
                                                        key={week}
                                                        onClick={() => hasCount ? setSelectedCellData({ sku: row.sku, week, details: cellData.details }) : null}
                                                        className={`px-3 py-2 text-center border-r transition-colors ${
                                                            hasCount
                                                                ? isPast
                                                                    ? 'bg-amber-100 font-bold text-amber-900 cursor-pointer hover:bg-amber-200 ring-1 ring-inset ring-amber-300'
                                                                    : isCurrent
                                                                    ? 'bg-purple-200 font-bold text-purple-950 cursor-pointer hover:bg-purple-300 ring-1 ring-inset ring-purple-300'
                                                                    : 'bg-purple-100 font-bold text-purple-900 cursor-pointer hover:bg-purple-300'
                                                                : isCurrent
                                                                ? 'bg-purple-50/40 text-gray-400'
                                                                : isPast
                                                                ? 'bg-gray-50/60 text-gray-400'
                                                                : 'text-gray-400'
                                                        }`}>
                                                        {cellData?.count || 0}
                                                    </td>
                                                );
                                            })}
                                            <td className="px-4 py-2 text-center font-bold text-gray-700 bg-gray-50">
                                                {rowTotal}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                            <tfoot className="bg-gray-50 font-bold text-gray-900">
                                <tr>
                                    <td className="px-4 py-3 text-right sticky left-0 bg-gray-50 border-r">{t.total_licenses}</td>
                                    <td className="px-4 py-3 text-center border-r">{grandTotal}</td>
                                    {weeks.map(week => (
                                        <td key={week} className="px-3 py-3 text-center border-r">
                                            {weekTotals[week] || 0}
                                        </td>
                                    ))}
                                    <td className="px-4 py-3 text-center bg-gray-100 border-t-2 border-gray-300">
                                        {grandTotal}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            )}

            {/* Modal Popup for Cell Details */}
            {selectedCellData && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex justify-center items-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl xl:max-w-7xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
                        <div className="p-4 border-b flex justify-between items-center bg-gray-50 rounded-t-lg">
                            <h3 className="text-xl font-bold text-gray-800" title={selectedCellData.sku}>
                                {t.customer_details || 'Customer Details'}: {formatSkuName(selectedCellData.sku)}
                            </h3>
                            <button onClick={() => setSelectedCellData(null)} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                            </button>
                        </div>

                        <div className="p-4 overflow-y-auto">
                            <div className="mb-4 flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <span className="bg-purple-100 text-purple-800 text-sm font-semibold px-3 py-1 rounded-full">
                                        Week: {selectedCellData.week.replace(/^\d{4}-W/, '')}
                                        {currentWeek && selectedCellData.week === currentWeek && ` (${t.current_week || 'Current Week'})`}
                                        {currentWeek && selectedCellData.week < currentWeek && ` (${getPastWeekOffset(selectedCellData.week, currentWeek)})`}
                                    </span>
                                    {currentWeek && selectedCellData.week < currentWeek && (
                                        <span className="bg-amber-100 text-amber-800 text-xs font-medium px-2 py-0.5 rounded-full">
                                            {t.past_week || 'Past Week'}
                                        </span>
                                    )}
                                </div>
                                <span className="text-sm text-gray-500">{selectedCellData.details.length} Subscription(s) found</span>
                            </div>

                            <div className="shadow-sm rounded-lg overflow-hidden border border-gray-200">
                                <table className="min-w-full text-sm transition-colors cursor-default" style={{ borderCollapse: 'collapse' }}>
                                    <thead className="bg-[#8b236a] text-white select-none">
                                        <tr>
                                            <th
                                                onClick={() => handleCellSort('customer')}
                                                className="px-4 py-3 text-left font-medium cursor-pointer hover:bg-[#721c56] transition-colors group"
                                            >
                                                <div className="flex items-center gap-1.5">
                                                    <span>{t.customer_name || 'Customer Name'}</span>
                                                    <SortIcon direction={cellSortConfig.key === 'customer' ? cellSortConfig.direction : null} isWhite />
                                                </div>
                                            </th>
                                            <th
                                                onClick={() => handleCellSort('domain')}
                                                className="px-4 py-3 text-left font-medium cursor-pointer hover:bg-[#721c56] transition-colors group"
                                            >
                                                <div className="flex items-center gap-1.5">
                                                    <span>{t.domain || 'Domain'}</span>
                                                    <SortIcon direction={cellSortConfig.key === 'domain' ? cellSortConfig.direction : null} isWhite />
                                                </div>
                                            </th>
                                            <th
                                                onClick={() => handleCellSort('licenses')}
                                                className="px-4 py-3 text-center font-medium cursor-pointer hover:bg-[#721c56] transition-colors group"
                                            >
                                                <div className="flex items-center justify-center gap-1.5">
                                                    <span>{t.total_licenses || 'Total'}</span>
                                                    <SortIcon direction={cellSortConfig.key === 'licenses' ? cellSortConfig.direction : null} isWhite />
                                                </div>
                                            </th>
                                            <th
                                                onClick={() => handleCellSort('renewalDate')}
                                                className="px-4 py-3 text-left font-medium cursor-pointer hover:bg-[#721c56] transition-colors group"
                                            >
                                                <div className="flex items-center gap-1.5">
                                                    <span>{t.renewal_date || 'Renewal Date'}</span>
                                                    <SortIcon direction={cellSortConfig.key === 'renewalDate' ? cellSortConfig.direction : null} isWhite />
                                                </div>
                                            </th>
                                            <th
                                                onClick={() => handleCellSort('renewalType')}
                                                className="px-4 py-3 text-center font-medium cursor-pointer hover:bg-[#721c56] transition-colors group"
                                            >
                                                <div className="flex items-center justify-center gap-1.5">
                                                    <span>{t.renewal_type_col || 'Renewal Type'}</span>
                                                    <SortIcon direction={cellSortConfig.key === 'renewalType' ? cellSortConfig.direction : null} isWhite />
                                                </div>
                                            </th>
                                            <th
                                                onClick={() => handleCellSort('status')}
                                                className="px-4 py-3 text-center font-medium cursor-pointer hover:bg-[#721c56] transition-colors group"
                                            >
                                                <div className="flex items-center justify-center gap-1.5">
                                                    <span>{t.status || 'Status'}</span>
                                                    <SortIcon direction={cellSortConfig.key === 'status' ? cellSortConfig.direction : null} isWhite />
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white">
                                        {sortedCellDetails.map((sub, i) => {
                                            const daysUntil = getDaysUntil(sub.renewalDate);
                                            const autoRenewTypes = ['AUTO_RENEW', 'RENEW_CURRENT_COMMITMENT', 'AUTO_RENEW_YEARLY_PAY', 'AUTO_RENEW_MONTHLY_PAY'];
                                            const isAutoRenew = 
                                                autoRenewTypes.includes(sub.renewalType) ||
                                                (typeof sub.renewalType === 'string' && sub.renewalType.toUpperCase().includes('AUTO_RENEW')) ||
                                                sub.showRenewalReminder === false;
                                            const showReminder = !isAutoRenew && daysUntil <= 30 && daysUntil >= -21;

                                            let activeTemplate = null;
                                            if (showReminder && t.renewal_reminders) {
                                                const reminders = t.renewal_reminders;
                                                if (daysUntil <= -7) {
                                                    activeTemplate = reminders.lost;
                                                } else if (daysUntil <= 0) {
                                                    activeTemplate = reminders.days_0;
                                                } else if (daysUntil <= 2) {
                                                    activeTemplate = reminders.days_2;
                                                } else if (daysUntil <= 7) {
                                                    activeTemplate = reminders.days_7;
                                                } else if (daysUntil <= 15) {
                                                    activeTemplate = reminders.days_15;
                                                } else if (daysUntil <= 30) {
                                                    activeTemplate = reminders.days_30;
                                                }
                                            }

                                            return (
                                                <React.Fragment key={i}>
                                                    <tr
                                                        className={`hover:bg-purple-50 transition-colors cursor-pointer group ${!activeTemplate ? 'border-b border-gray-200' : ''}`}
                                                        onClick={() => handleCustomerClick(sub.customerId)}
                                                        title="Click to view on dashboard"
                                                    >
                                                        <td className="px-4 py-3 font-medium text-gray-900 border-t border-gray-200 transition-colors">
                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                <span>{sub.customerName || sub.customerDomain}</span>
                                                                <PostpaidPrepaidBadge isPostpaid={sub.isPostpaid} t={t} />
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3 text-gray-600 border-t border-gray-200 transition-colors">{sub.customerDomain}</td>
                                                        <td className="px-4 py-3 text-center font-bold text-purple-700 border-t border-gray-200 transition-colors">{sub.licenses}</td>
                                                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap border-t border-gray-200 transition-colors">
                                                            <div className="flex items-center">
                                                                <span>{sub.renewalDate}</span>
                                                                {isAutoRenew ? (
                                                                    <span className="ml-3 px-1.5 py-0.5 rounded text-xs font-bold text-red-500 border border-red-500 uppercase whitespace-nowrap">
                                                                        {t.auto_renew || 'Auto renew'}
                                                                    </span>
                                                                ) : showReminder ? (
                                                                    <div className={`ml-3 flex items-center ${daysUntil < 0 ? 'text-amber-700 border-amber-300 bg-amber-50' : 'text-red-600 border-red-200 bg-red-50'} border px-2 py-1 rounded text-xs font-semibold shadow-sm whitespace-nowrap`}>
                                                                        <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                                                                        {daysUntil < 0
                                                                            ? (t.renew_days_ago ? t.renew_days_ago.replace('{days}', Math.abs(daysUntil)) : `${Math.abs(daysUntil)} ${language === 'nl' ? 'dagen geleden' : 'days ago'}`)
                                                                            : (t.renew_reminder ? t.renew_reminder.replace('{days}', daysUntil) : `Renew in ${daysUntil} days`)}
                                                                    </div>
                                                                ) : null}
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3 text-center border-t border-gray-200 transition-colors whitespace-nowrap">
                                                            <RenewalTypeBadge renewalType={sub.renewalType} />
                                                        </td>
                                                        <td className="px-4 py-3 text-center border-t border-gray-200 transition-colors whitespace-nowrap">
                                                            <SubscriptionStatusBadge status={sub.status} />
                                                        </td>
                                                    </tr>
                                                    {activeTemplate && (
                                                        <tr className="hover:bg-purple-50 transition-colors border-b border-gray-200">
                                                            <td colSpan="6" className="px-6 pb-3 pt-0 transition-colors">
                                                                <div className="text-sm text-gray-700 italic border-l-4 border-red-400 pl-4 py-2.5 bg-red-50 rounded-r-lg w-full">
                                                                    <span dangerouslySetInnerHTML={{ __html: activeTemplate }} className="block w-full" />
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="p-4 border-t bg-gray-50 rounded-b-lg flex justify-end">
                            <button
                                onClick={() => setSelectedCellData(null)}
                                className="px-5 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 font-medium transition-colors"
                            >
                                {t.close_details || 'Close'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Quick Information Details Modal for Suspended / Going to Suspend */}
            {quickInfoModalType && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-6xl xl:max-w-7xl w-full max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
                        {/* Modal Header */}
                        <div className={`p-4 border-b flex justify-between items-center ${
                            quickInfoModalType === 'suspended' ? 'bg-rose-50 border-rose-200' : 'bg-amber-50 border-amber-200'
                        }`}>
                            <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-lg ${
                                    quickInfoModalType === 'suspended' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'
                                }`}>
                                    {quickInfoModalType === 'suspended' ? (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    ) : (
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    )}
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900">
                                        {quickInfoModalType === 'suspended'
                                            ? (t.quick_info_suspended_modal_title || 'Suspended Customers (±2 weeks)')
                                            : (t.quick_info_going_suspend_modal_title || 'Customers Going to Suspend (±2 weeks)')}
                                    </h3>
                                    <p className="text-xs text-gray-500">
                                        {filteredModalList.length} {t.quick_info_customers || 'subscriptions'} • {filteredModalList.reduce((acc, s) => acc + (s.licenses || 0), 0)} {t.quick_info_licenses || 'licenses'}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => { setQuickInfoModalType(null); setQuickModalSearch(''); setModalStatusFilter(null); }}
                                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>

                        {/* Modal Search & Status Filter */}
                        <div className="p-3 border-b bg-gray-50/50 flex flex-col md:flex-row items-center justify-between gap-3 flex-wrap">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2.5 w-full md:w-auto flex-wrap">
                                <input
                                    type="text"
                                    value={quickModalSearch}
                                    onChange={(e) => setQuickModalSearch(e.target.value)}
                                    placeholder={t.search_customer || 'Search customer or domain...'}
                                    className="w-full sm:w-72 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8b236a] focus:border-transparent"
                                />
                                {modalAvailableStatuses.length > 1 && (
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="text-xs font-bold text-gray-400 uppercase tracking-wider select-none shrink-0 mr-1">
                                            {t?.status || 'Status'}:
                                        </span>
                                        <button
                                            onClick={() => setModalStatusFilter(null)}
                                            className={`px-3 py-1 rounded-full text-xs font-bold border transition duration-200 shrink-0 select-none ${
                                                getStatusChipClasses('ALL', modalStatusFilter === null)
                                            }`}
                                        >
                                            {t?.all_statuses || t?.filter_all || 'Alles'}
                                        </button>
                                        {modalAvailableStatuses.map(status => {
                                            const isActive = modalStatusFilter === status;
                                            return (
                                                <button
                                                    key={status}
                                                    onClick={() => setModalStatusFilter(isActive ? null : status)}
                                                    className={`px-3 py-1 rounded-full text-xs font-bold border transition duration-200 shrink-0 select-none ${
                                                        getStatusChipClasses(status, isActive)
                                                    }`}
                                                >
                                                    {status}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={() => {
                                    setDaysFilter(quickInfoModalType === 'suspended' ? 'suspended_2w' : 'going_suspend_2w');
                                    if (modalStatusFilter) {
                                        setStatusFilter(modalStatusFilter);
                                    }
                                    setQuickInfoModalType(null);
                                    setQuickModalSearch('');
                                    setModalStatusFilter(null);
                                }}
                                className={`px-3 py-1.5 text-xs font-semibold rounded-lg text-white shadow-xs transition-colors w-full sm:w-auto ${
                                    quickInfoModalType === 'suspended' ? 'bg-rose-700 hover:bg-rose-800' : 'bg-amber-700 hover:bg-amber-800'
                                }`}
                            >
                                {t.quick_info_filter_table || 'Filter Table with this list'}
                            </button>
                        </div>

                        {/* Modal Body / Table */}
                        <div className="p-4 overflow-y-auto max-h-[60vh]">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                                <thead className="bg-gray-50 text-gray-600 uppercase text-xs font-semibold select-none sticky top-0 z-10 border-b">
                                    <tr>
                                        <th
                                            onClick={() => handleModalSort('customer')}
                                            className="px-4 py-2.5 text-left cursor-pointer hover:bg-gray-100 transition-colors group"
                                        >
                                            <div className="flex items-center gap-1.5">
                                                <span>{t.customer_name || 'Customer'}</span>
                                                <SortIcon direction={modalSortConfig.key === 'customer' ? modalSortConfig.direction : null} />
                                            </div>
                                        </th>
                                        <th
                                            onClick={() => handleModalSort('sku')}
                                            className="px-4 py-2.5 text-left cursor-pointer hover:bg-gray-100 transition-colors group"
                                        >
                                            <div className="flex items-center gap-1.5">
                                                <span>{t.sku || 'SKU'}</span>
                                                <SortIcon direction={modalSortConfig.key === 'sku' ? modalSortConfig.direction : null} />
                                            </div>
                                        </th>
                                        <th
                                            onClick={() => handleModalSort('week')}
                                            className="px-3 py-2.5 text-center cursor-pointer hover:bg-gray-100 transition-colors group"
                                        >
                                            <div className="flex items-center justify-center gap-1.5">
                                                <span>Week</span>
                                                <SortIcon direction={modalSortConfig.key === 'week' ? modalSortConfig.direction : null} />
                                            </div>
                                        </th>
                                        <th
                                            onClick={() => handleModalSort('licenses')}
                                            className="px-3 py-2.5 text-center cursor-pointer hover:bg-gray-100 transition-colors group"
                                        >
                                            <div className="flex items-center justify-center gap-1.5">
                                                <span>{t.total_licenses || 'Licenses'}</span>
                                                <SortIcon direction={modalSortConfig.key === 'licenses' ? modalSortConfig.direction : null} />
                                            </div>
                                        </th>
                                        <th
                                            onClick={() => handleModalSort('renewalDate')}
                                            className="px-4 py-2.5 text-left cursor-pointer hover:bg-gray-100 transition-colors group"
                                        >
                                            <div className="flex items-center gap-1.5">
                                                <span>Renewal Date</span>
                                                <SortIcon direction={modalSortConfig.key === 'renewalDate' ? modalSortConfig.direction : null} />
                                            </div>
                                        </th>
                                        <th
                                            onClick={() => handleModalSort('renewalType')}
                                            className="px-3 py-2.5 text-center cursor-pointer hover:bg-gray-100 transition-colors group"
                                        >
                                            <div className="flex items-center justify-center gap-1.5">
                                                <span>{t.renewal_type_col || 'Renewal Type'}</span>
                                                <SortIcon direction={modalSortConfig.key === 'renewalType' ? modalSortConfig.direction : null} />
                                            </div>
                                        </th>
                                        <th
                                            onClick={() => handleModalSort('status')}
                                            className="px-3 py-2.5 text-center cursor-pointer hover:bg-gray-100 transition-colors group"
                                        >
                                            <div className="flex items-center justify-center gap-1.5">
                                                <span>{t.status || 'Status'}</span>
                                                <SortIcon direction={modalSortConfig.key === 'status' ? modalSortConfig.direction : null} />
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 bg-white">
                                    {sortedModalList.length === 0 ? (
                                        <tr>
                                            <td colSpan="7" className="px-4 py-8 text-center text-gray-400">
                                                No records found.
                                            </td>
                                        </tr>
                                    ) : (
                                        sortedModalList.map((item, idx) => {
                                            const daysUntil = getDaysUntil(item.renewalDate);
                                            return (
                                                <tr
                                                    key={idx}
                                                    onClick={() => handleCustomerClick(item.customerId)}
                                                    className="hover:bg-purple-50/70 transition-colors cursor-pointer group"
                                                    title="Click to view customer on dashboard"
                                                >
                                                    <td className="px-4 py-2.5 font-medium text-gray-900 group-hover:text-primary">
                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                            <span>{item.customerName || item.customerDomain}</span>
                                                            <PostpaidPrepaidBadge isPostpaid={item.isPostpaid} t={t} />
                                                            <svg className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 text-primary transition-opacity flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                                                        </div>
                                                        {item.customerName && item.customerDomain && item.customerName !== item.customerDomain && (
                                                            <div className="text-[11px] font-normal text-gray-500 font-mono mt-0.5">{item.customerDomain}</div>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-2.5 text-gray-700">{formatSkuName(item.sku)}</td>
                                                    <td className="px-3 py-2.5 text-center">
                                                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                                            item.weekOffset < 0 ? 'bg-amber-100 text-amber-800' : item.weekOffset === 0 ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                                                        }`}>
                                                            {item.week.split('-W')[1]} {item.weekOffset < 0 ? `(${item.weekOffset}w)` : item.weekOffset === 0 ? '(NU)' : `(+${item.weekOffset}w)`}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-2.5 text-center font-bold text-gray-900">{item.licenses}</td>
                                                    <td className="px-4 py-2.5 text-gray-600 whitespace-nowrap">
                                                        <div>{item.renewalDate}</div>
                                                        <div className="text-[11px] text-gray-400">
                                                            {daysUntil < 0
                                                                ? `${Math.abs(daysUntil)} ${language === 'nl' ? 'dagen geleden' : 'days ago'}`
                                                                : `${daysUntil} ${language === 'nl' ? 'dagen over' : 'days left'}`}
                                                        </div>
                                                    </td>
                                                    <td className="px-3 py-2.5 text-center whitespace-nowrap">
                                                        <RenewalTypeBadge renewalType={item.renewalType} />
                                                    </td>
                                                    <td className="px-3 py-2.5 text-center whitespace-nowrap">
                                                        <SubscriptionStatusBadge status={item.status} />
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Modal Footer */}
                        <div className="p-3 border-t bg-gray-50 flex justify-end">
                            <button
                                onClick={() => { setQuickInfoModalType(null); setQuickModalSearch(''); }}
                                className="px-4 py-1.5 text-sm font-medium bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                            >
                                {t.close_details || 'Close'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminRenewals;
